/**
 * Centralized form options and value mappings for contact forms
 */

export const ORDER_VOLUME_OPTIONS = [
  { value: 'NOT_LIVE_YET', label: 'Not live yet' },
  { value: 'OPT0_500', label: '0-500 / month' },
  { value: 'OPT500_2_5K', label: '500-2.5k / month' },
  { value: 'OPT2_5_10K', label: '2.5-10k / month' },
  { value: 'OPT10K_100K', label: '10k-100k / month' },
  { value: 'OPT100K_1M', label: '100k-1m / month' },
] as const

export const COMPANY_SIZE_OPTIONS = [
  { value: 'SMALL', label: '1-10 employees' },
  { value: 'MEDIUM', label: '11-100 employees' },
  { value: 'LARGE', label: '101-500 employees' },
  { value: 'XLARGE', label: '500+ employees' },
] as const

export const INTERESTED_IN_OPTIONS = [
  {
    value: 'MANAGED_HOSTING',
    label: 'Managed Hosting',
    description: 'Enterprise-grade hosting with Kubernetes',
  },
  {
    value: 'SUPPORT_SERVICES',
    label: 'Support Services',
    description: 'Consulting, development and training.',
  },
  {
    value: 'COMMERCIAL_EDITION',
    label: 'Commercial License',
    description: 'Commercial license with priority support.',
  },
] as const

export const USE_CASE_OPTIONS = [
  { value: 'D2C', label: 'Direct to Consumer (D2C)' },
  { value: 'B2B', label: 'Business-to-Business (B2B)' },
  { value: 'MULTIVENDOR', label: 'Multi-Vendor' },
  { value: 'CUSTOMSOFTWARE', label: 'Custom Software' },
  { value: 'SUBSCRIPTIONS', label: 'Subscriptions' },
  { value: 'OTHER', label: 'Other' },
] as const

/**
 * Create a mapping object from options array
 */
function createMapping<T extends readonly { value: string; label: string }[]>(
  options: T,
): Record<string, string> {
  return Object.fromEntries(options.map((opt) => [opt.value, opt.label]))
}

/**
 * Value to label mappings for all form fields
 */
export const FORM_VALUE_MAPPINGS = {
  orderVolume: createMapping(ORDER_VOLUME_OPTIONS),
  companySize: createMapping(COMPANY_SIZE_OPTIONS),
  interestedIn: createMapping(INTERESTED_IN_OPTIONS),
  useCase: createMapping(USE_CASE_OPTIONS),
} as const

/**
 * Convert form values to human-readable labels
 */
export function formatFormValue(key: string, value: string | number | string[] | boolean): string {
  if (Array.isArray(value)) {
    const mapping = FORM_VALUE_MAPPINGS[key as keyof typeof FORM_VALUE_MAPPINGS]
    if (mapping) {
      return value.map((v) => mapping[v] || v).join(', ')
    }
    return value.join(', ')
  }

  if (typeof value === 'string') {
    const mapping = FORM_VALUE_MAPPINGS[key as keyof typeof FORM_VALUE_MAPPINGS]
    if (mapping && mapping[value]) {
      return mapping[value]
    }
  }

  return String(value)
}
